import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild, ViewChildren, ViewEncapsulation, inject } from '@angular/core';
import { Observable, Subject, catchError, debounceTime, filter, lastValueFrom, map, of, startWith, switchMap, takeUntil, tap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RequestSenderIdFormGroup } from '../../../interfaces/forms/request-sender-id-form.interface';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { SenderIdsFormService } from '../../../services/sender-ids-form.service';
import { SenderIdsFormComponent } from '../../sender-ids-form/sender-ids-form.component';
import { SenderIdsOpasFormService } from '../../../services/sender-ids-opas-form.service';
import { SenderIdsOpasFormComponent } from '../../sender-ids-opas-form/sender-ids-opas-form.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { AccountApiService } from '../../../services/account-api.service';
import { PublicApiService } from '../../../services/public-api.service';
import { SenderIdAuthorisationItem } from '../../../interfaces/sender-id-authorisation-item.interface';
import { SpinnerModule } from 'common';
import { AvailableSidoItem } from '../../../interfaces/available-sido-item';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { autocompleteValueValidator } from '../../../validators/autocomplete-value.validator';
import { CreateSenderIdItem } from '../../../interfaces/create-sender-id-item.interface';
import { ThirdPartyAssignmentItem } from '../../../interfaces/third-party-assignement-item.interface';

export interface RequestSenderIdDialogData {
  showOwnerSelect?: boolean;
}

@Component({
  selector: 'si-request-sender-id-dialog',
  templateUrl: './request-sender-id-dialog.component.html',
  styleUrls: ['./request-sender-id-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatDialogModule, MatStepperModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCheckboxModule, SenderIdsFormComponent, SenderIdsOpasFormComponent, MatRadioModule, MatSelectModule, SpinnerModule, MatAutocompleteModule],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    }
  ],
})
export class RequestSenderIdDialogComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') private stepper: MatStepper;

  private readonly fb = inject(FormBuilder);
  private readonly senderIdsFormService = inject(SenderIdsFormService);
  private readonly senderIdsOpasFormService = inject(SenderIdsOpasFormService);
  private readonly accountApiService = inject(AccountApiService);
  private readonly publicApiService = inject(PublicApiService);
  public readonly data = inject<RequestSenderIdDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<RequestSenderIdDialogComponent>);

  private _destroy$ = new Subject<void>();
  public senderIdForm: RequestSenderIdFormGroup;
  public opasList: SenderIdAuthorisationItem[] = [];
  public sidoList: AvailableSidoItem[] = [];
  public isFetching = true;
  public isSaving = false;
  public filteredSidoOptions$: Observable<AvailableSidoItem[]>;
  public thirdParties: ThirdPartyAssignmentItem[];
  private selectedThirdParties: string[][] = [];
  public showSuccessSummary = false;
  public serverErrorMessage: string = null;

  public ngOnInit(): void {
    this.getInitialData();

    this.senderIdForm = this.fb.group({
      owner: new FormControl(false),
      sido: new FormControl<null | string | AvailableSidoItem>({ disabled: true, value: null }),
      texts: this.senderIdsFormService.initFormArray(true, 0),
      opas: this.senderIdsOpasFormService.initFormArray(0),
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
  }

  public async getInitialData() {
    this.opasList = await lastValueFrom(this.publicApiService.getAllOpasList$());
    this.thirdParties = await lastValueFrom(this.accountApiService.getThirdParties$());

    this.addSubscriptions();

    this.isFetching = false;
  }

  public addSubscriptions() {
    this.getFormControl('owner')
      .valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((isSelected: boolean) => {
        const sidoIdControl = this.getFormControl('sido');
        if (isSelected) {
          sidoIdControl.enable();
          sidoIdControl.addValidators([Validators.required, autocompleteValueValidator()]);
        } else {
          sidoIdControl.disable();
          sidoIdControl.clearValidators();
          sidoIdControl.setValue(null);
        }

        sidoIdControl.updateValueAndValidity();
      });

    this.filteredSidoOptions$ = this.getFormControl('sido')
      .valueChanges
      .pipe(
        startWith(''),
        takeUntil(this._destroy$),
        debounceTime(600),
        filter((value) => value && (typeof value === 'string' && value.length >= 2)),
        switchMap((value: string) => {
          return this.accountApiService.getAvailableSidoList$(value).pipe(
            catchError(() => of([])),
          );
        }),
      )
  }

  public getFormControl<T>(name: string): FormArray {
    return this.senderIdForm.get(name) as FormArray<FormControl<T>>;
  }

  public get opaControlsArray() {
    return this.getFormControl<boolean>('opas');
  }

  public get textsControlsArray(): FormArray {
    return this.getFormControl<string>('texts');
  }

  public handleRequestButtonClick() {
    this.opaControlsArray.markAsDirty();
  }

  public displayFn(option: AvailableSidoItem) {
    return option?.organisationName ?? '';
  }

  public handleThirdPartiesChanged(selectedThirdParties: string[][]) {
    this.selectedThirdParties = selectedThirdParties;
  }

  public async handleFormSubmit() {
    if (this.senderIdForm.invalid) return;

    const { texts, opas, sido, owner } = this.senderIdForm.getRawValue();
    const opaList = this.opasList.filter((_, index) => opas[index].selected).map(({ accountId }) => ({ accountId }));

    if (typeof sido === 'string') return;

    this.isSaving = true;

    const senderIdList: CreateSenderIdItem[] = texts.map((text, index) => ({
      senderIdText: text,
      thirdPartyIdList: this.thirdParties.filter(thirdParty => (this.selectedThirdParties[index] ?? []).includes(thirdParty.organisationName)).map(thirdParty => thirdParty.thirdPartyID),
    }));

    try {
      let result = await lastValueFrom(
        this.accountApiService.addSenderIds$({
          opaList,
          senderIdList,
          ...owner && {
            onBehalfOfAccountId: sido.accountId,
          },
        })
      );
      if (result.success) {
        this.showSuccessSummary = true;
        this.serverErrorMessage = null;
        //this.dialogRef.close(true);
      } else {
        this.showSuccessSummary = false;
        this.serverErrorMessage = result.errors.join(";");
      }
    } finally {
      this.isSaving = false;
    }
  }
}
